/**
 * CardsTab - Main container for the Cards tab
 *
 * Uses the same rollup data as TeamsTab for consistency.
 * The teams prop contains all the card data in the windows object.
 */
import React, { useState } from 'react'
import type { TeamRollupOut, CardsContextResponse, CardWindowKey, FixtureClassificationContext } from '../types'
import { TeamCardStatsCard } from './TeamCardStatsCard'
import { RefereeCardStatsCard } from './RefereeCardStatsCard'
import { H2HCardStatsCard } from './H2HCardStatsCard'
import { PlayerCardTables } from './PlayerCardTable'

interface CardsTabProps {
  teams: TeamRollupOut[] | null
  cardsContext: CardsContextResponse | null
}

type WindowKey = 'season' | 'last10' | 'last5' | 'last3'

const windowLabel: Record<WindowKey, string> = {
  season: 'Season',
  last10: 'L10',
  last5: 'L5',
  last3: 'L3',
}

/**
 * Enhanced context banner for the Cards tab
 */
function CardsContextBanner({ classification }: { classification: FixtureClassificationContext | null }) {
  if (!classification) return null

  const hasClassification =
    classification.is_derby || classification.is_relegation_battle || classification.is_title_race

  if (!hasClassification) return null

  // Build the tags
  const tags: { label: string; color: string; bgColor: string }[] = []

  if (classification.is_derby) {
    tags.push({
      label: classification.derby_name || 'Derby Match',
      color: 'var(--red)',
      bgColor: 'rgba(239, 68, 68, 0.15)',
    })
  }

  if (classification.is_title_race) {
    tags.push({
      label: 'Title Race',
      color: 'var(--purple)',
      bgColor: 'rgba(139, 92, 246, 0.15)',
    })
  }

  if (classification.is_relegation_battle) {
    tags.push({
      label: 'Relegation Battle',
      color: 'var(--yellow)',
      bgColor: 'rgba(251, 191, 36, 0.15)',
    })
  }

  return (
    <div
      style={{
        padding: '12px 16px',
        background: 'linear-gradient(135deg, var(--card) 0%, rgba(255,255,255,0.03) 100%)',
        borderRadius: 8,
        borderLeft: `4px solid ${tags[0]?.color || 'var(--accent)'}`,
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        {tags.map((tag, i) => (
          <span
            key={i}
            style={{
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              color: tag.color,
              background: tag.bgColor,
            }}
          >
            {tag.label}
          </span>
        ))}
      </div>
      <p className="small" style={{ margin: 0 }}>
        High-stakes matches typically see elevated card counts due to increased intensity.
      </p>
    </div>
  )
}

export function CardsTab({ teams, cardsContext }: CardsTabProps) {
  const [window, setWindow] = useState<WindowKey>('season')

  // Primary data source is teams rollup (same as TeamsTab)
  if (!teams || teams.length < 2) {
    return (
      <div className="small" style={{ marginTop: 10 }}>
        No rollups. Ingest finished fixtures and run rollups.
      </div>
    )
  }

  const homeTeam = teams[0]
  const awayTeam = teams[1]

  // Extract card context data if available (for referee, h2h, players, classification)
  const classification = cardsContext?.classification ?? null
  const referee = cardsContext?.referee ?? null
  const h2h = cardsContext?.h2h ?? null
  const players = cardsContext?.players ?? null

  return (
    <div className="cards-tab">
      {/* Match context banner */}
      <CardsContextBanner classification={classification} />

      {/* Window filter chips - same style as Teams tab */}
      <div className="tab-filter-bar">
        {(['season', 'last10', 'last5', 'last3'] as WindowKey[]).map((w) => (
          <button
            key={w}
            className={`chip ${window === w ? 'chip-active' : ''}`}
            onClick={() => setWindow(w)}
          >
            {windowLabel[w]}
          </button>
        ))}
      </div>

      {/* Team card stats - using teams rollup data directly */}
      <TeamCardStatsCard
        homeTeam={homeTeam}
        awayTeam={awayTeam}
        window={window}
      />

      {/* Referee stats - from cards context */}
      {referee && (
        <RefereeCardStatsCard
          referee={referee}
          homeTeam={homeTeam.team}
          awayTeam={awayTeam.team}
        />
      )}

      {/* H2H card history - from cards context */}
      {h2h && (
        <H2HCardStatsCard
          h2h={h2h}
          homeTeam={homeTeam.team}
          awayTeam={awayTeam.team}
          window={window}
        />
      )}

      {/* Player card tables - from cards context */}
      {players && (
        <PlayerCardTables
          homePlayers={players.home}
          awayPlayers={players.away}
          homeTeam={homeTeam.team}
          awayTeam={awayTeam.team}
          window={window as CardWindowKey}
          lineupConfirmed={players.lineup_confirmed}
        />
      )}
    </div>
  )
}
